import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { useLanguage } from '../../lib/i18n';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_creative-ai-studio-151/artifacts/0wsobm4i_zsdigitals%20logo.jpeg';

const primaryLinks = [
  { key: 'services', path: '/services' },
  { key: 'portfolio', path: '/portfolio' },
  { key: 'caseStudies', path: '/case-studies' },
];

const secondaryLinks = [
  { key: 'about', path: '/about' },
  { key: 'careers', path: '/careers' },
  { key: 'contact', path: '/contact' },
];

function useScrollState() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return scrolled;
}

function useClickOutside(ref, handler) {
  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) handler(); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [ref, handler]);
}

function NavLink({ path, label, currentPath }) {
  return (
    <Link
      to={path}
      className={`px-2.5 lg:px-3 py-2 text-xs lg:text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
        currentPath === path ? 'text-[#7C3AED] bg-[#7C3AED]/10' : 'text-white/70 hover:text-white hover:bg-white/5'
      }`}
    >
      {label}
    </Link>
  );
}

function MoreDropdown({ links, currentPath, t, isRTL }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(ref, close);
  const location = useLocation();
  useEffect(() => setOpen(false), [location]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 px-2.5 lg:px-3 py-2 text-xs lg:text-sm font-medium rounded-full transition-all duration-200 ${
          links.some(l => currentPath === l.path) ? 'text-[#7C3AED] bg-[#7C3AED]/10' : 'text-white/70 hover:text-white hover:bg-white/5'
        }`}
        data-testid="nav-more-btn"
      >
        {t('nav.more')} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.15 }}
            className={`absolute top-full mt-2 w-40 bg-[#12121C] border border-white/10 rounded-xl shadow-[0_20px_40px_rgba(0,0,0,0.5)] overflow-hidden ${isRTL ? 'right-0' : 'left-0'}`}
          >
            {links.map((link) => (
              <Link key={link.path} to={link.path} className={`block px-4 py-2.5 text-sm transition-colors ${currentPath === link.path ? 'text-[#7C3AED] bg-[#7C3AED]/5' : 'text-white/70 hover:text-white hover:bg-white/5'}`}>
                {t(`nav.${link.key}`)}
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileNav({ links, currentPath, t }) {
  return (
    <div className="px-4 py-3 space-y-0.5 max-h-[70vh] overflow-y-auto">
      {links.map((link) => (
        <Link key={link.path} to={link.path} className={`block px-4 py-3 rounded-xl text-sm font-medium transition-all ${currentPath === link.path ? 'text-[#7C3AED] bg-[#7C3AED]/10' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}>
          {t(`nav.${link.key}`)}
        </Link>
      ))}
      <Link to="/contact" className="block px-4 py-3 bg-[#7C3AED] text-white text-sm font-semibold rounded-xl text-center mt-2">
        {t('nav.consultation')}
      </Link>
    </div>
  );
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const scrolled = useScrollState();
  const { t, lang, toggleLang, isRTL } = useLanguage();

  useEffect(() => { setMobileOpen(false); }, [location]);

  const allLinks = [
    { key: 'home', path: '/' },
    ...primaryLinks,
    ...secondaryLinks,
    { key: 'contact', path: '/contact' },
  ];

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-[#050508]/90 border-b border-[#7C3AED]/10 shadow-[0_4px_30px_rgba(124,58,237,0.05)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0" data-testid="logo-link">
            <img src={LOGO_URL} alt="ZS Digitals" className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg object-contain" />
            <span className="font-poppins font-bold text-base sm:text-lg text-white hidden sm:inline">ZS Digitals</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-0.5 lg:gap-1" data-testid="desktop-nav">
            <NavLink path="/" label={t('nav.home')} currentPath={location.pathname} />
            {primaryLinks.map((link) => (
              <NavLink key={link.path} path={link.path} label={t(`nav.${link.key}`)} currentPath={location.pathname} />
            ))}
            <MoreDropdown links={secondaryLinks} currentPath={location.pathname} t={t} isRTL={isRTL} />
          </nav>

          {/* Right side: Lang toggle + CTA + mobile menu */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={toggleLang} data-testid="lang-toggle" className={`flex items-center gap-1.5 rounded-full border text-xs font-bold transition-all ${lang === 'en' ? 'px-3 py-1.5 bg-[#7C3AED]/15 border-[#7C3AED]/40 text-[#C4B5FD] hover:bg-[#7C3AED]/25 hover:border-[#7C3AED]/60 min-w-[68px] justify-center' : 'px-3 py-1.5 bg-white/5 border-white/10 text-white/80 hover:bg-white/10 hover:border-[#7C3AED]/30'}`}>
              <Globe className="w-3.5 h-3.5" />{lang === 'en' ? 'عربي' : 'EN'}
            </button>
            <Link to="/contact" data-testid="header-cta-button" className="hidden sm:inline-flex px-4 lg:px-6 py-2 bg-[#7C3AED] text-white text-xs lg:text-sm font-semibold rounded-full hover:bg-[#6D28D9] hover:shadow-[0_8px_25px_rgba(124,58,237,0.4)] transition-all duration-300">
              {t('nav.consultation')}
            </Link>
            <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2 rounded-lg text-white hover:bg-white/5 transition-colors" data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#050508]/98 backdrop-blur-xl border-b border-[#7C3AED]/10" data-testid="mobile-nav">
            <MobileNav links={allLinks} currentPath={location.pathname} t={t} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
